import { BEATS_PER_MEASURE, Key, TICKS_PER_LOOP } from '@arpeggi/shared'
import { flow, Instance, types } from 'mobx-state-tree'
import { nanoid } from 'nanoid'
import { ForDirectExtend } from '../../../../features/mobx'
import { Arp2Utils } from '../../../client/SongUtils'
import { ticksToSeconds } from '../../../components/studio/StudioUtils'
import { getAudioData } from '../sample/SampleUtils'
import { ISampleWithEffectsStore, SampleWithEffectsStore } from '../sample/SampleWithEffectsStore'

export const STRETCH_RANGE = [0.1, 3]
const MINIMUM_CLIP_LENGTH = 2

const _ClipStore = types
  .model({
    sampleWithEffects: types.maybe(SampleWithEffectsStore),
    // where exactly on the song timeline the clip should start
    startInComposition48n: 0,
    // where exactly on the song timeline the clip should end
    endInComposition48n: MINIMUM_CLIP_LENGTH,
    // how many ticks at the beginning of the base audio sample have been trimmed off
    // 0 <= offset48n < getAudioSourceDuration48n() - MINIMUM_CLIP_LENGTH
    offset48n: 0,
    // useful for tracking since clips can be moved between stems
    uniqueId: types.maybe(types.string),
    // generally means the sample hasn't loaded yet if it's initializing
    initializing: false,
    // whether the clip should snap to grid when resizing/cutting/dragging
    snapToGrid: true
  })
  .views((self) => ({
    getSample(): ISampleWithEffectsStore {
      return self.sampleWithEffects
    },
    get audioData(): any {
      return getAudioData(self.sampleWithEffects?.sampleIndex.toString()).audioData
    },
    /** the section number that the clip ends in */
    get endSection(): number {
      return Math.floor(self.endInComposition48n / TICKS_PER_LOOP)
    },
    /** the semitone for the clip, used when calculating the audio graph */
    get semitone(): number {
      return Arp2Utils.getMiddleC() + self.sampleWithEffects?.transpose
    },
    get transpose(): number {
      return self.sampleWithEffects?.transpose
    },
    get warp(): number {
      return self.sampleWithEffects?.warp
    },
    get reverse(): boolean {
      return self.sampleWithEffects?.reverse
    }
  }))
  .actions((self) => {
    /** stores the audio duration for whatever the audio source is, right now only sampleWithEffects */
    let parentAudioDurationInSeconds

    async function afterCreate() {
      self.initializing = true
      if (!self.uniqueId) generateClipID()
      if (self.sampleWithEffects) {
        parentAudioDurationInSeconds = await self.sampleWithEffects?.getFullSampleDurationSeconds()
      }
      self.initializing = false
    }

    function setUniqueId(uniqueId: string) {
      self.uniqueId = uniqueId
    }

    function generateClipID() {
      setUniqueId(nanoid())
    }

    /**
     * Once a clip has been initialized with a certain SampleWithEffectsStore, it is tied
     * to the sample in that SampleWithEffectsStore. A new clip must be made in order to change
     * the sample used in it. This is due to the async nature (and difficulties) of loading
     * sample audioBuffers and still providing a good UX
     */
    const init = flow(function* (
      sampleWithEffects: ISampleWithEffectsStore,
      tempo: number,
      start48n: number = 0
    ) {
      self.initializing = true
      self.sampleWithEffects = sampleWithEffects
      parentAudioDurationInSeconds = yield sampleWithEffects.getFullSampleDurationSeconds()
      self.startInComposition48n = start48n
      self.endInComposition48n = start48n + getAudioSourceDuration48n(tempo)
      self.initializing = false
    })

    function getAudioSourceDurationInSeconds() {
      return (
        (self.sampleWithEffects?.end - self.sampleWithEffects?.start) *
        parentAudioDurationInSeconds *
        self.warp
      )
    }

    /**
     * Right now only gets the duration the sampleWithEffects, but should be updated
     * as we begin to support other sound sources
     */
    function getAudioSourceDuration48n(tempo: number) {
      const secondsLength = getAudioSourceDurationInSeconds()
      const barLengthInSeconds = 4 * (BEATS_PER_MEASURE / tempo) * 60
      const sampleLengthInBars = secondsLength / barLengthInSeconds
      return TICKS_PER_LOOP * sampleLengthInBars
    }

    ///////////////////////// Setters /////////////////////

    function updateOffset(delta48n: number) {
      self.offset48n = self.offset48n + delta48n
    }
    function updateStartInComposition(start: number) {
      self.startInComposition48n = start
    }
    function updateEndInComposition(end: number) {
      self.endInComposition48n = end
    }
    function updateTranspose(transpose: number) {
      return self.sampleWithEffects?.updateTranspose(transpose)
    }
    function updateWarp(warp: number) {
      const currentWarp = self.sampleWithEffects?.warp
      const warpRatio = warp / currentWarp
      self.endInComposition48n =
        (self.endInComposition48n - self.startInComposition48n) * warpRatio +
        self.startInComposition48n
      self.offset48n *= warpRatio
      return self.sampleWithEffects?.updateWarp(warp)
    }
    function updateReverse(shouldReverse: boolean, tempo: number) {
      if (self.reverse === shouldReverse) return
      const songLengthTicks = getAudioSourceDuration48n(tempo)
      const newOffset =
        self.startInComposition48n - self.offset48n + songLengthTicks - self.endInComposition48n
      self.offset48n = newOffset
      return self.sampleWithEffects.updateReverse(shouldReverse)
    }

    ///////////////////////// Warp //////////////////////////////////

    /**
     * For clips that have a BPM for the underlying sound, this will warp it
     * to have a bpm that matches the tempo
     * @param tempo The tempo to match the BPM to
     */
    function matchTempo(tempo: number) {
      const arpMedia = self.sampleWithEffects.getArpMedia()
      if (!arpMedia || arpMedia.metadata.bpm === 0) return

      updateWarp(tempo / arpMedia.metadata.bpm)
    }

    ///////////////////////// Movement/Resizing /////////////////////

    /**
     * Called when moving the handle on the left side of the clip
     * @returns whether any update happened successfully
     * */
    function moveLeftSide48n(delta48n: number, tempo: number): boolean {
      let value = self.startInComposition48n + delta48n

      if (value < 0) {
        value = 0
      }
      if (value >= self.endInComposition48n - MINIMUM_CLIP_LENGTH) {
        value = self.endInComposition48n - MINIMUM_CLIP_LENGTH
      }
      // the clip can't be longer than the audio source's length
      if (self.endInComposition48n - value > getAudioSourceDuration48n(tempo)) {
        value = self.endInComposition48n - getAudioSourceDuration48n(tempo)
      }
      // offset cannot be less than 0
      let processedDelta48n = value - self.startInComposition48n
      if (self.offset48n + processedDelta48n < 0) {
        processedDelta48n = -self.offset48n
        value = self.startInComposition48n + processedDelta48n
      }

      const valueChanged = self.startInComposition48n !== value
      updateStartInComposition(value)
      updateOffset(processedDelta48n)
      return valueChanged
    }

    /**
     * Called when moving the handle on the right side of the clip
     * @returns whether any update happened successfully
     * */
    function moveRightSide48n(delta48n: number, tempo: number, songLengthIn48n: number): boolean {
      let value = self.endInComposition48n + delta48n

      if (value >= songLengthIn48n) {
        value = songLengthIn48n
      }
      // if the new end is less than the clip start, it cannot be done
      if (value <= self.startInComposition48n + MINIMUM_CLIP_LENGTH) {
        value = self.startInComposition48n + MINIMUM_CLIP_LENGTH
      }
      // if the new end would make the clip longer than the audio duration, it cannot be done
      if (value - self.startInComposition48n + self.offset48n > getAudioSourceDuration48n(tempo)) {
        value = getAudioSourceDuration48n(tempo) - self.offset48n + self.startInComposition48n
      }
      const valueChanged = self.endInComposition48n !== value
      updateEndInComposition(value)
      return valueChanged
    }

    /**
     * Called when moving the clip
     * @returns whether any update happened successfully
     * */
    function move48n(delta48n: number, songLengthIn48n: number): boolean {
      let processeddDelta48n = delta48n
      if (self.startInComposition48n + delta48n < 0) {
        processeddDelta48n = -self.startInComposition48n
      }
      if (self.endInComposition48n + delta48n > songLengthIn48n) {
        processeddDelta48n = songLengthIn48n - self.endInComposition48n
      }
      self.startInComposition48n = self.startInComposition48n + processeddDelta48n
      self.endInComposition48n = self.endInComposition48n + processeddDelta48n
      return processeddDelta48n !== 0
    }

    /**
     * Called when moving the stretch handle on the right side of the clip
     * @returns whether any update happened successfully
     * */
    function warpRightSide48n(delta48n: number, tempo: number, songLengthIn48n: number): boolean {
      let value = self.endInComposition48n + delta48n

      if (value >= songLengthIn48n) {
        value = songLengthIn48n
      }
      // if the new end is less than the clip start, it cannot be done
      if (value <= self.startInComposition48n + MINIMUM_CLIP_LENGTH) {
        value = self.startInComposition48n + MINIMUM_CLIP_LENGTH
      }
      // if the new end would make the clip longer than the song, it cannot be done
      if (value > songLengthIn48n) {
        value = songLengthIn48n
      }
      let newWarp =
        ((value - self.startInComposition48n) /
          (self.endInComposition48n - self.startInComposition48n)) *
        self.warp
      // if the value is outside the bounds of the max/min stretch values, it cannot be done
      if (newWarp < STRETCH_RANGE[0]) {
        newWarp = STRETCH_RANGE[0]
      }
      if (newWarp > STRETCH_RANGE[1]) {
        newWarp = STRETCH_RANGE[1]
      }

      const valueChanged = self.warp !== newWarp
      updateWarp(newWarp)
      return valueChanged
    }

    /**
     * moves a clip's start to the beggining of a certain section
     * @param section the section to move the clip to
     * @param songLengthIn48n the length of the current song in 48n
     * @returns whether the movement was successful or not
     */
    function moveToSection(section: number, songLengthIn48n: number): boolean {
      if (section < 0 || songLengthIn48n / TICKS_PER_LOOP < section) return false
      const currentStart = self.startInComposition48n
      return move48n(section * TICKS_PER_LOOP - currentStart, songLengthIn48n)
    }

    /**
     * ONLY USE IF YOU ABSOLUTELY KNOW WHAT YOU'RE DOING, otherwise use moveLeftSide48n().
     * Sets the start position of the clip
     */
    function setStartInteral(ticks: number) {
      self.startInComposition48n = ticks
    }
    /**
     * ONLY USE IF YOU ABSOLUTELY KNOW WHAT YOU'RE DOING, otherwise use moveRightSide48n().
     * Sets the end position of the clip
     * */
    function setEndInteral(ticks: number) {
      self.endInComposition48n = ticks
    }
    /**
     * ONLY USE IF YOU ABSOLUTELY KNOW WHAT YOU'RE DOING, otherwise use moveLeftSide48n().
     * Sets the offset position of the clip
     * */
    function setOffsetInteral(ticks: number) {
      self.offset48n = ticks
    }

    ///////////////////////// Tempo /////////////////////

    /**
     * Called when updating the tempo of the parent song, as this will
     * change the length of clips
     */
    function updateTempo(oldTempo: number, newTempo: number) {
      const oldLength = getAudioSourceDuration48n(oldTempo)
      const newLength = getAudioSourceDuration48n(newTempo)
      const ratio = newLength / oldLength
      setOffsetInteral(self.offset48n * ratio)
      setEndInteral(
        self.startInComposition48n + (self.endInComposition48n - self.startInComposition48n) * ratio
      )
    }

    ///////////////////////// Transpose /////////////////////

    function transposeUp() {
      return updateTranspose(self.sampleWithEffects?.transpose + 1)
    }

    function transposeDown() {
      return updateTranspose(self.sampleWithEffects?.transpose - 1)
    }

    /**
     * Updates the clip's key to match the input key
     * @param newKey the new key for the clip to be
     */
    function updateKey(newKey: Key) {
      const sound = self.sampleWithEffects.getArpMedia()
      let keyOffset = newKey - sound.metadata.key
      // Put KeyOffset in range [-6, 6] so we don't shift things too much.
      if (keyOffset > 6) keyOffset -= 12
      if (keyOffset < -6) keyOffset += 12
      updateTranspose(keyOffset)
    }

    ////////////////////// Grid Snapping /////////////////////

    function toggleSnapToGrid() {
      self.snapToGrid = !self.snapToGrid
      return self.snapToGrid
    }

    //////////////////// Getters //////////////////////////

    function startOffsetPercent(tempo: number): number {
      return (100 * ticksToSeconds(self.offset48n, tempo)) / parentAudioDurationInSeconds
    }

    function endOffsetPercent(tempo: number): number {
      const widthTicks = self.endInComposition48n - self.startInComposition48n + self.offset48n
      const warpedDurationSeconds = self.warp * parentAudioDurationInSeconds
      return (
        (100 * (warpedDurationSeconds - ticksToSeconds(widthTicks, tempo))) / warpedDurationSeconds
      )
    }

    return {
      init,
      updateStartInComposition,
      updateEndInComposition,
      updateTranspose,
      updateWarp,
      updateReverse,
      transposeUp,
      transposeDown,
      afterCreate,
      setUniqueId,
      move48n,
      moveLeftSide48n,
      moveRightSide48n,
      getAudioSourceDurationInSeconds,
      getAudioSourceDuration48n,
      updateTempo,
      updateOffset,
      generateClipID,
      moveToSection,
      setStartInteral,
      setEndInteral,
      setOffsetInteral,
      warpRightSide48n,
      updateKey,
      toggleSnapToGrid,
      startOffsetPercent,
      endOffsetPercent,
      matchTempo
    }
  })
  .views((self) => ({
    get canBeStretchedOrTransposed(): boolean {
      return true
      // return self.getAudioSourceDurationInSeconds() / self.warp < 30
    }
  }))

interface ClipStoreType extends ForDirectExtend<typeof _ClipStore> {}
export const ClipStore: ClipStoreType = _ClipStore

export interface IClipStore extends ForDirectExtend<Instance<typeof ClipStore>> {}
